import Vue from "vue";

const loaderCache = new WeakMap(); // 每个动态 import 函数只缓存一次，避免切换菜单时重复请求同一个页面 chunk。

const isLazyImportComponent = (component) => {
  // Vue 构造器也是 function，但有 cid/options；这里只处理路由里 `() => import()` 这类懒加载函数。
  return typeof component === "function" && !component.cid && !component.options;
};

const loadRouteComponent = (loader) => {
  // 把动态 import 的 promise 缓存起来，外壳组件和 hover 预加载可以共享同一次加载结果。
  if (!loaderCache.has(loader)) {
    const cached = loader().then((module) => module.default || module);
    loaderCache.set(loader, cached);
  }
  return loaderCache.get(loader);
};

const getRouteEnterHook = (component) => {
  // Vue.extend 后钩子可能在 options 上，普通 SFC 对象则直接挂在组件对象上。
  return component?.beforeRouteEnter || component?.options?.beforeRouteEnter;
};

export const preloadLazyRouteComponent = (component) => {
  // 菜单 hover/空闲预热只触发懒加载组件；同步组件直接跳过。
  const loader = component?.__lazyRouteLoader || component;
  if (!isLazyImportComponent(loader)) {
    return Promise.resolve();
  }
  return loadRouteComponent(loader).catch(() => {});
};

const createRouteShell = (loader, route) => {
  const shellName = `RouteLazyShell${route.name || route.path || "Anonymous"}`;
  const shellComponent = Vue.extend({
    name: shellName,
    data() {
      return {
        loadedComponent: null,
        loadError: null,
        routeEnterFrom: null,
        routeEnterHandled: false,
      };
    },
    beforeRouteEnter(to, from, next) {
      // 外壳先让路由确认，保存 from 给真实组件恢复列表状态时使用。
      next((vm) => {
        vm.routeEnterFrom = from;
        vm.runRouteEnterHook(to);
      });
    },
    created() {
      // 路由已经确认后再加载真实页面组件，让 URL 和菜单状态不再等 chunk 下载完成。
      loadRouteComponent(loader)
        .then((component) => {
          this.loadedComponent = component;
          this.$nextTick(() => this.runRouteEnterHook(this.$route));
        })
        .catch((error) => {
          this.loadError = error;
          console.error(error);
        });
    },
    methods: {
      runRouteEnterHook(to) {
        // 真实组件可能用 beforeRouteEnter 的 next(vm) 恢复页面状态；等拿到 from 后再补执行这个回调。
        const hook = getRouteEnterHook(this.loadedComponent);
        if (!hook || this.routeEnterHandled || !this.routeEnterFrom) {
          return;
        }
        this.routeEnterHandled = true;
        hook(to, this.routeEnterFrom || {}, (callbackOrLocation) => {
          if (typeof callbackOrLocation === "function") {
            this.$nextTick(() => {
              if (this.$refs.routeView) {
                callbackOrLocation(this.$refs.routeView);
              }
            });
          } else if (callbackOrLocation) {
            this.$router.replace(callbackOrLocation);
          }
        });
      },
    },
    render(h) {
      if (this.loadError) {
        return h("div", { staticClass: "route-lazy-shell route-lazy-shell-error" }, "页面加载失败，请刷新重试");
      }
      if (!this.loadedComponent) {
        return h("div", { staticClass: "route-lazy-shell route-lazy-shell-loading" });
      }
      return h(this.loadedComponent, {
        ref: "routeView",
        attrs: this.$attrs,
        on: this.$listeners,
        scopedSlots: this.$scopedSlots,
      });
    },
  });
  shellComponent.__lazyRouteLoader = loader; // 保留原始 loader，供侧边栏 hover/空闲预加载继续复用。
  return shellComponent;
};

export const normalizeSidebarLazyRoutes = (routes = []) => {
  routes.forEach((route) => {
    const shouldUseShell = route.meta?.sidebar && !route.meta?.series;
    if (shouldUseShell && isLazyImportComponent(route.component)) {
      // 侧边栏入口改成同步外壳组件，避免 Vue Router 3 在确认导航前等待异步组件下载。
      route.component = createRouteShell(route.component, route);
    }
    if (route.children?.length) {
      normalizeSidebarLazyRoutes(route.children);
    }
  });
};
